/**
 * Route utilities for determining default navigation paths based on permissions.
 */

/**
 * Core modules to check as fallbacks when dashboard is not permitted.
 * Order determines priority - first permitted module wins.
 */
const FALLBACK_MODULES = [
  "screener",
  "reports",
  "playground",
  "documents",
  "ops",
] as const;

export type DefaultRouteOptions = {
  /** Classic layout: home module alone is not enough — require ani_os or forced OS. */
  requireOsForAgenticHome?: boolean;
  forceAniOs?: boolean;
};

function canLandOnAgenticHome(
  hasPermission: (module: string) => boolean,
  options?: DefaultRouteOptions,
): boolean {
  if (!hasPermission("home")) {
    return false;
  }
  if (!options?.requireOsForAgenticHome) {
    return true;
  }
  return Boolean(options.forceAniOs) || hasPermission("ani_os");
}

/**
 * Determines the default route for a user based on their permissions.
 *
 * Priority order:
 * 1. initial_route from permissions (if set by backend)
 * 2. /home (home module + ani_os when requireOsForAgenticHome)
 * 3. /dashboard (if user has dashboard permission)
 * 4. /advisor-dashboard (advisor_dashboard module)
 * 5. First permitted module from FALLBACK_MODULES
 * 6. "/" as final fallback
 */
export function resolveOsDefaultLandingPath(
  _companyId: string | null | undefined,
  initialRoute: string | undefined,
  hasPermission: (module: string) => boolean,
  options?: DefaultRouteOptions,
): string {
  return getDefaultRoute(initialRoute, hasPermission, options);
}

export function getDefaultRoute(
  initialRoute: string | undefined,
  hasPermission: (module: string) => boolean,
  options?: DefaultRouteOptions,
): string {
  if (initialRoute) {
    return initialRoute;
  }

  if (canLandOnAgenticHome(hasPermission, options)) {
    return "/home";
  }

  if (hasPermission("dashboard")) {
    return "/dashboard";
  }

  if (hasPermission("advisor_dashboard")) {
    return "/advisor-dashboard";
  }

  const firstPermitted = FALLBACK_MODULES.find((m) => hasPermission(m));
  if (firstPermitted) {
    return `/${firstPermitted}`;
  }

  return "/";
}

/**
 * Gets the list of fallback modules in priority order.
 */
export function getFallbackModules(): readonly string[] {
  return FALLBACK_MODULES;
}

/** Classic shell: agentic /home only when ani_os (or company forces OS). */
export function agenticHomeRouteOptions(forceAniOs = false): DefaultRouteOptions {
  return { requireOsForAgenticHome: true, forceAniOs };
}
